import {ActionIcon, Paper, Select, Switch} from "@mantine/core";
import {NumberInput} from "@mantine/core";
import { TrashIcon } from "@phosphor-icons/react";
import {useCallback, useEffect, useMemo, useState} from "react";
import {v4 as uuidv4} from "uuid";
import withRouteErrorBoundary from "../../components/ErrorBoundary/withRouteErrorBoundary.tsx";
import {Row, Col} from "../../components/Flex/Flex";
import LoaderScreen from "../../components/Loader/LoaderScreen.tsx";
import Page from "../../components/Page/Page.tsx";
import {useEntityTypes, useUser} from "../../utils/api.ts";
import style from "./Cargo.module.css";
import {CargoBar, DockingBayIcon, HangarBayIcon} from "./Icons.tsx";
import SharedSave from "../../components/SharedSave/SharedSave.tsx";
import {useParams} from "react-router";
import useSavedItem from "../../components/SharedSave/useSavedItem.ts";
import {useSaveFitout} from "../../components/SharedSave/useSaveFitout.ts";
import {StatChanges, StatUpdateDialog} from "./StatUpdateDialog.tsx";
import {DroidType, FacilityType, ItemType, MaterialType, ShipType, StationType, VehicleType} from "../../types/swc.types.ts";

const filterContainerFacilities = (facility: FacilityType) => facility.volumecap > 1000;


type ContainerEntity = ShipType | FacilityType | VehicleType | StationType;
type CargoEntity = ShipType | VehicleType | MaterialType | DroidType | ItemType;
type CargoType = {
    uuid: string,
    qty: number | null,
    entity: CargoEntity,
    type: string
}

const mapToOption = (entity: CargoEntity|ContainerEntity) => {
    return {label: entity.name, value: entity.id};
}


function FitoutCalculator() {
    const {id} = useParams();

    const {ships, vehicles, stations, materials, facilities, items, droids, isPending} = useEntityTypes(['ships', 'vehicles', 'stations', 'materials', 'facilities', 'items', 'droids'], true);
    const [cargo, setCargo] = useState<CargoType[]>([]);
    const [container, setContainer] = useState<ContainerEntity | null>(null);
    const [asSquad, setAsSquad] = useState(false);
    const {user} = useUser();
    const [updatedEntities, setUpdatedEntities] = useState<StatChanges[]>([]);
    const saveMutation = useSaveFitout('fitout', id);

    const savedData = useSavedItem('fitout', id);
    const [lastProcessedId, setLastProcessedId] = useState<string | null>(null);

    useEffect(() => {
        if (savedData && savedData.content && !isPending && lastProcessedId !== savedData.id) {
            const changed = new Set<StatChanges>();
            setCargo(savedData.content.cargo.map((c: CargoType) => {
                let id = c.entity.id;
                let [t] = id.split(':');
                let entity: CargoEntity | undefined;
                if (t === '2') {
                    entity = ships.find(s => s.id === id);
                } else if (t === '3') {
                    entity = vehicles.find(s => s.id === id);
                } else if (t === '16') {
                    entity = materials.find(s => s.id === id);
                } else if (t === '12') {
                    entity = items.find(s => s.id === id);
                } else if (t === '13') {
                    entity = droids.find(s => s.id === id);
                }
                if (entity) {
                    if (entity.weight !== c.entity.weight || entity.volume !== c.entity.volume) {
                        changed.add({
                            name: entity.name,
                            mode: 'cargo',
                            prevWeight: entity.weight,
                            newWeight: c.entity.weight,
                            prevVolume: entity.volume,
                            newVolume: c.entity.volume
                        });
                    }
                    return {...c, entity};
                }
                return c;
            }));

            if (savedData.content.container) {
                let id = savedData.content.container.id;
                let [t] = id.split(':');
                let entity: ContainerEntity | undefined
                if (t === '2') {
                    entity = ships.find(s => s.id === id);
                } else if (t === '3') {
                    entity = vehicles.find(s => s.id === id);
                } else if (t === '4') {
                    entity = facilities.find(s => s.id === id);
                } else if (t === '5') {
                    entity = stations.find(s => s.id === id);
                }
                if (entity) {
                    const old = savedData.content.container;
                    if (('weightcap' in entity && 'weightcap' in old && entity.weightcap !== old.weightcap) ||
                        entity.volumecap !== old.volumecap) {
                        changed.add({
                            name: entity.name,
                            mode: 'container',
                            prevWeight: 'weightcap' in entity ? entity.weightcap : undefined,
                            newWeight: 'weightcap' in old ? old.weightcap : undefined,
                            prevVolume: entity.volumecap,
                            newVolume: old.volumecap
                        });
                    }
                    setContainer(entity);
                } else {
                    setContainer(savedData.content.container);
                }
            } else {
                setContainer(null);
            }

            setAsSquad(savedData.content.asSquad);
            setLastProcessedId(savedData.id);
            if (changed.size > 0 && savedData.owner === user.uid) {
                setUpdatedEntities(Array.from(changed));
            }
        }
    }, [savedData, isPending, ships, vehicles, stations, materials, facilities, items, droids]);


    const cargoOptions = useMemo(() => {
        const groups: {group: string, items: any[]}[] = [];
        if (container === null || container.dockingbay) {
            groups.push({group: 'Ships', items: ships.map(mapToOption)})
        }
        if (container === null || container.hangarbay) {
            groups.push({group: 'Vehicles', items: vehicles.map(mapToOption)});
        }
        groups.push({group: 'Materials', items: materials.map(mapToOption)});
        groups.push({group: 'Droids (Crated)', items: droids.map(mapToOption)});
        groups.push({group: 'Items', items: items.map(mapToOption)});

        return groups;
    }, [ships, vehicles, materials, container]);


    const containerOptions = useMemo(() => {
        return [
            {id: 'ships', key: 'ships', group: 'Ships', items: ships.map(mapToOption)},
            {id: 'vehicles', key: 'vehicles', group: 'Vehicles', items: vehicles.map(mapToOption)},
            {id: 'stations', key: 'stations', group: 'Stations', items: stations.map(mapToOption)},
            {id: 'facilities', key: 'facilities', group: 'Facilities', items: facilities.filter(filterContainerFacilities).map(mapToOption)},
        ]
    }, [ships, vehicles, materials, stations, facilities]);

    const addEntity = useCallback((id: string) => {
        let [t] = id.split(':');
        let entity: CargoEntity | undefined;
        let type: string;
        if (t === '2') {
            entity = ships.find(s => s.id === id);
            type = 'ship';
        } else if (t === '3') {
            entity = vehicles.find(s => s.id === id);
            type = 'vehicle';
        } else if (t === '16') {
            entity = materials.find(s => s.id === id);
            type = 'material';
        } else if(t === '12') {
            entity = items.find(s => s.id === id);
            type = 'item';
        } else if(t === '13') {
            entity = droids.find(s => s.id === id);
            type = 'droid';
        } else {
            console.error('Missing type map for ', id);
            type = 'not found';
        }
        if (typeof entity !== 'undefined') {
            setCargo(prev => [...prev, {uuid: uuidv4() as string, entity, qty: null, type}]);
        } else {
            console.error('Entity', type, id, 'not found');
        }
    }, [ships, vehicles, materials, items, droids]);

    const updateQty = useCallback((uuid: string, qty: number | null) => {
        setCargo(prev => {
            let next = [...prev];
            let i = next.findIndex(c => c.uuid === uuid);
            next[i] = {...next[i], qty};
            return next;
        })
    }, []);
    const removeCargo = useCallback((uuid: string) => {
        setCargo(prev => prev.filter(c => c.uuid !== uuid));
    }, [])


    const totals = useMemo(() => {
        return cargo.reduce((tot, cargo) => {
            let w = tot.weight + ((cargo.qty ?? 0) * cargo.entity.weight);
            let v = tot.volume + ((cargo.qty ?? 0) * cargo.entity.volume);
            let ships = tot.ships || (cargo.type === 'ship');
            let vehicles = tot.vehicles || (cargo.type === 'vehicles');
            return {weight: w, volume: v, ships, vehicles};
        }, {weight: 0, volume: 0, ships: false, vehicles: false});
    }, [cargo]);

    const getContainerData = useCallback((id: string) => {
        let [t] = id.split(':');
        let entity: ContainerEntity | undefined
        if (t === '2') {
            entity = ships.find(s => s.id === id);
        } else if (t === '3') {
            entity = vehicles.find(s => s.id === id);
        } else if (t === '4') {
            entity = facilities.find(s => s.id === id);
        } else if (t === '5') {
            entity = stations.find(s => s.id === id);
        } else {
            console.error('Missing type map for ', id);
        }
        if (typeof entity !== 'undefined') {
            return entity;
        } else {
            return null
        }
    }, [ships, vehicles, facilities, stations]);

    const squadNum: number = useMemo(() => {
        if (!asSquad) {
            return 1;
        }
        if (!container || !('slotsize' in container)) {
            return 1;
        }
        return Math.floor(12 / container.slotsize);
    }, [container, asSquad]);

    const currentData = useMemo(() => ({cargo, container, asSquad}), [cargo, container, asSquad]);



    return (
        <Page>
            <title>Fitout Calculator</title>
            {isPending ? <LoaderScreen message="Loading data"/> : null}
            <Row align="center" justify="between">
                <Col>
                    <h2>Fitout Calculator</h2>
                    {savedData ? <h3>{savedData.name} <small>({savedData.ownerName})</small></h3> : null}
                </Col>
                <Col>
                    <SharedSave type="fitout" content={currentData} savedData={savedData}/>
                </Col>
            </Row>

            <Paper shadow="xs" p="md" withBorder>
                <Row wrap={false} mobileCol={true}>
                    <Col basis="auto">
                        <Select
                            style={{marginBottom: '0.5rem'}}
                            data={containerOptions}
                            value={container?.id}
                            onChange={val => { if (val) setContainer(getContainerData(val)); }}
                            searchable
                            placeholder="Select Container"
                        />
                        <Select
                            style={{marginBottom: '0.5rem'}}
                            data={cargoOptions}
                            value={null}
                            onChange={val => { if (val) addEntity(val); }}
                            searchable
                            placeholder="Add Cargo"
                            disabled={container === null}
                        />
                    </Col>
                    <Col grow={1}>
                        {container !== null ? (
                            <Container container={container} totals={totals} asSquad={asSquad} setAsSquad={setAsSquad} squad={squadNum}/>
                        ) : null}
                    </Col>
                </Row>

                <div className={style.tableWrapper}>
                    <table className={style.fitoutTable}>
                        <thead style={{textAlign: 'left'}}>
                        <tr>
                            <th></th>
                            <th>Entity</th>
                            <th>Quantity</th>
                            <th>Can still fit</th>
                            <th>Max fit</th>
                            <th/>
                        </tr>
                        </thead>
                        <tbody>
                        {cargo.map((c) => <CargoRow key={c.uuid} cargo={c} totals={totals} container={container} squad={squadNum} updateQty={updateQty}
                                                    remove={removeCargo}/>)}
                        </tbody>
                    </table>
                </div>

            </Paper>

            <StatUpdateDialog
              changes={updatedEntities}
              clear={() => setUpdatedEntities([])}
              save={() => saveMutation.mutate({id, type: 'cargo', content: currentData}, {
                  onSuccess: () => setUpdatedEntities([])
              })}
            />
        </Page>
    )

}

type CargoRowProps = {
    cargo: CargoType,
    container: ContainerEntity | null,
    totals: any,
    remove: any,
    squad: number,
    updateQty: (uuid: string, qty: number | null) => void
}
const CargoRow = ({cargo, container, totals, remove, updateQty, squad}: CargoRowProps) => {
    if (container === null) {
        return null;
    }
    const canFitW = 'weightcap' in container ? Math.floor((squad * container.weightcap - totals.weight) / cargo.entity.weight) : Infinity;
    const canFitV = Math.floor((squad * container.volumecap - totals.volume) / cargo.entity.volume);
    const canFit = Math.min(canFitV, canFitW);
    const maxFit = canFit + (cargo.qty ?? 0);

    const totalFitW = 'weightcap' in container ? squad * Math.floor(container.weightcap / cargo.entity.weight) : Infinity;
    const totalFitV = squad * Math.floor(container.volumecap / cargo.entity.volume);
    const totalFit = Math.min(totalFitW, totalFitV);

    const error = (cargo.type === 'ship' && !container.dockingbay) || (cargo.type === 'vehicle' && !container.hangarbay);
    return (
        <tr>
            <td><img className={style.cargoImg} src={cargo.entity.images.small} alt={cargo.entity.name}/></td>
            <td>
                <div className={error ? 'text-red' : ''}>{cargo.entity.name}</div>
                <small>{cargo.entity.volume.toLocaleString()}m<sup>3</sup><br/>{cargo.entity.weight.toLocaleString()}T</small>
            </td>
            <td><NumberInput value={cargo.qty ?? ''} onChange={val => {
                const numVal = typeof val === 'number' ? val : null;
                updateQty(cargo.uuid, (numVal !== null && numVal > maxFit) ? maxFit : numVal);
            }} size="xs" min={0} max={maxFit} style={{width: '5em'}}/></td>
            <td>{canFit.toLocaleString()}</td>
            <td>{totalFit.toLocaleString()}</td>
            <td><ActionIcon variant="subtle" onClick={() => remove(cargo.uuid)}><TrashIcon size={16}/></ActionIcon></td>
        </tr>
    )
}


type ContainerProps = {
    container: ContainerEntity,
    totals: any,
    squad: number,
    asSquad: boolean,
    setAsSquad: (s: boolean) => void
}

const Container = ({container, totals, squad, asSquad, setAsSquad}: ContainerProps) => {
    const errorHangar = !container.hangarbay && totals.vehicles ? (<div>
        Container does not have Hangar bay to house vehicles!
    </div>) : null;
    const errorDock = !container.dockingbay && totals.ships ? (<div>
        Container does not have Docking bay to house ships!
    </div>) : null;

    const maxWeight = 'weightcap' in container ? squad * container.weightcap : Infinity;
    const maxVolume = squad * container.volumecap;

    const errorWeight = totals.weight > maxWeight ? <div>Total weight ({totals.weight.toLocaleString()}T) exceeds capacity ({maxWeight.toLocaleString()}T)!</div> : null;
    const errorVolume = totals.volume > maxVolume ? <div>Total volume ({totals.volume.toLocaleString()}m<sup>3</sup>) exceeds capacity ({maxVolume.toLocaleString()}m<sup>3</sup>)!</div> : null;

    return (
        <>
            <Row wrap={true}>
                {container.images?.small ? <Col><img className={style.fitoutImg} src={container.images.small} alt={container.name}/></Col> : null}
                <Col grow={1}>
                    <b>{container.name}</b><br/>
                    <Row>
                        <DockingBayIcon value={container.dockingbay}/>
                        <HangarBayIcon value={container.hangarbay}/>
                    </Row>
                    <br/>
                    {'slotsize' in container && container.slotsize <= 6 ?
                        <div>
                            <Switch id="asSquad" checked={asSquad} onChange={e => setAsSquad(e.currentTarget.checked)}
                                    label={`Use as full squad (${Math.floor(12 / container.slotsize)})`}/>
                        </div>
                        : null}
                </Col>
                <Col>
                    {'weightcap' in container ? <CargoBar label="Weight" value={totals.weight} max={squad * container.weightcap}/> : null}
                    <CargoBar label="Volume" value={totals.volume} max={squad * container.volumecap}/>
                </Col>
            </Row>
            <div className="text-red">
                {errorHangar}
                {errorDock}
                {errorWeight}
                {errorVolume}
            </div>
        </>
    )
}

export default withRouteErrorBoundary(FitoutCalculator);
